import React, { useEffect, useRef } from 'react'

import FileCard from 'components/Cards/File'
import SurveyVideoPrompt from 'components/SurveyVideoPrompt'
import WistiaPlayer from 'components/WistiaPlayer'
import { parseStrapiImage } from 'utils'
import { Icon } from 'ui/styled'
import { trackEvent } from 'services/analytics'

import {
  ArticleImage,
  AudioPoster,
  AudioShell,
  AudioElement,
  AudioTitle,
  BackButtonRow,
  FallbackPanel,
  MediaBody,
  MediaRoot,
  PdfShell,
} from './styled'
import Button from 'components/Form/Button'

export default function CourseDetailsLessonMedia({
  course,
  lesson,
  onBack,
  onVideoCompleted,
  surveyEndPrompt,
  userId,
}) {
  const podcastPlayedRef = useRef(false)

  useEffect(() => {
    podcastPlayedRef.current = false
  }, [lesson?.id])

  if (!lesson) return null

  const previewImage = lesson?.image?.url ? parseStrapiImage(lesson.image.url) : lesson?.cover
  const handlePodcastPlayed = () => {
    if (podcastPlayedRef.current) return
    podcastPlayedRef.current = true
    trackEvent('PODCAST_PLAYED', {
      content_type: 'audio',
      content_id: lesson.id,
      content_name: lesson.title,
      course_id: course?.id,
      course_name: course?.title,
    })
  }

  return (
    <MediaRoot>
      <BackButtonRow>
        <Button width='fit-content' startIcon={<Icon icon='arrow' width={24} />} small nospace color='blue' link onClick={onBack} type="button">
          <span>Back to Courses</span>
        </Button>
      </BackButtonRow>

      {lesson?.type === 'video' ? (
        <WistiaPlayer
          analytics={{
            eventPrefix: 'lesson_video',
            metadata: {
              video_name: lesson?.title || 'Lesson Video',
              video_type: 'lesson',
              lesson_id: lesson?.id,
              lesson_name: lesson?.title,
              course_id: course?.id,
              course_name: course?.title,
              user_id: userId,
              source: 'course_details',
              screen: 'course_details',
            },
            sessionKey: `${course?.id || 'course'}:${lesson?.id || lesson?.video_id || lesson?.video?.url || 'lesson'}`,
          }}
          course={lesson}
          endScreen={!surveyEndPrompt?.visible ? null : (
            <SurveyVideoPrompt
              survey={surveyEndPrompt.survey}
              onDismiss={surveyEndPrompt.onDismiss}
              onOpen={surveyEndPrompt.onOpen}
            />
          )}
          minHeight="420px"
          onCompleted={onVideoCompleted}
          showDetails={false}
        />
      ) : null}

      {lesson?.type === 'audio' ? (
        <MediaBody>
          <AudioShell>
            {!previewImage ? null : <AudioPoster src={previewImage} alt={lesson?.title} />}
            <div>
              <AudioTitle>{lesson?.title}</AudioTitle>
              <AudioElement controls onPlay={handlePodcastPlayed} preload="metadata">
                <source src={lesson?.audio?.url ? parseStrapiImage(lesson.audio.url) : ''} />
              </AudioElement>
            </div>
          </AudioShell>
        </MediaBody>
      ) : null}

      {lesson?.type === 'pdf' || lesson?.type === 'documents' ? (
        <MediaBody>
          <PdfShell>
            {!lesson?.file ? (
              <FallbackPanel>
                <Icon icon="pdf-big" />
                <div>No file available for this lesson.</div>
              </FallbackPanel>
            ) : (
              <FileCard file={lesson} />
            )}
          </PdfShell>
        </MediaBody>
      ) : null}

      {lesson?.type === 'article' ? (
        !previewImage ? (
          <MediaBody>
            <FallbackPanel>
              <Icon icon="article" />
              <div>Read the lesson overview below.</div>
            </FallbackPanel>
          </MediaBody>
        ) : (
          <ArticleImage src={previewImage} alt={lesson?.title} />
        )
      ) : null}
    </MediaRoot>
  )
}
